import type { RoundingMode } from '../schemas/project.js';

/**
 * Money math helpers for Phase 1.
 *
 * Strategy: keep intermediate math in `number`. Round to 2 decimals at every
 * boundary that produces a `Money` value (installment principal, interest,
 * total). Phase 1 amounts are bounded well below 2^53 / 100, so `number` is
 * safe — but we never persist or compare unrounded values.
 */

const SCALE = 100;

export function roundTo2(value: number, mode: RoundingMode): number {
  if (!Number.isFinite(value)) {
    throw new Error(`cannot round non-finite value: ${value}`);
  }
  const scaled = value * SCALE;
  const sign = scaled < 0 ? -1 : 1;
  const abs = Math.abs(scaled);
  const floor = Math.floor(abs);
  const frac = abs - floor;

  let rounded: number;
  const EPS = 1e-9;

  if (frac > 0.5 + EPS) {
    rounded = floor + 1;
  } else if (frac < 0.5 - EPS) {
    rounded = floor;
  } else {
    switch (mode) {
      case 'half_up':
        rounded = floor + 1;
        break;
      case 'half_down':
        rounded = floor;
        break;
      case 'half_even':
        rounded = floor % 2 === 0 ? floor : floor + 1;
        break;
    }
  }

  return (sign * rounded) / SCALE;
}

/** Format a `number` to a `Money` string ("12345.67") after rounding. */
export function toMoney(value: number, mode: RoundingMode): string {
  const rounded = roundTo2(value, mode);
  return rounded.toFixed(2);
}

/** Parse a Money string to a `number`. Trusts the caller to have validated. */
export function moneyToNumber(value: string): number {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    throw new Error(`invalid Money value: ${value}`);
  }
  return n;
}

/** Sum a list of Money strings, returning a Money string. No intermediate rounding. */
export function sumMoney(values: readonly string[], mode: RoundingMode): string {
  let total = 0;
  for (const v of values) total += moneyToNumber(v);
  return toMoney(total, mode);
}
